import { memo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import CrossIcon from './icons/cross.svg?react'
import PropTypes from 'prop-types';

/**
 * ConfirmationModal - A reusable confirmation dialog following the Design Language System
 * 
 * Provides a consistent confirmation pattern across the entire application with proper
 * accessibility, focus management, and DLS-compliant styling.
 * 
 * @component
 * @example
 * <ConfirmationModal
 *   isOpen={showDeleteConfirm}
 *   title="Delete Conversation"
 *   message="Are you sure you want to delete this conversation? This action cannot be undone."
 *   confirmText="Delete"
 *   cancelText="Cancel"
 *   variant="destructive"
 *   onConfirm={handleDelete}
 *   onCancel={handleCancel}
 *   onClose={handleClose}
 * />
 */
const ConfirmationModal = memo(({
  isOpen = false,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default', // 'default' | 'destructive'
  onConfirm,
  onCancel,
  onClose,
  isLoading = false
}) => {
  const handleConfirm = () => {
    onConfirm?.();
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose?.();
    }
  };

  // Variant-specific styling
  const isDestructive = variant === 'destructive';
  const confirmButtonClasses = isDestructive
    ? 'bg-error hover:bg-error/90 focus:ring-error/50 text-tertiary'
    : 'bg-accent hover:bg-hover focus:ring-accent/50 text-tertiary';

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] data-[state=open]:animate-fadeIn" 
        />
        
        {/* Modal Content */}
        <Dialog.Content className="fixed left-[50%] top-[50%] z-[10000] w-full max-w-md translate-x-[-50%] translate-y-[-50%] bg-bg-overlay border border-border-default rounded-lg shadow-elevation-3 focus:outline-none data-[state=open]:animate-fadeIn">
        
        {/* Header */}
        <div className="flex items-start justify-between px-spacing-4 pt-spacing-4">
          <div className="flex items-start gap-spacing-4">
            <div className="flex flex-col gap-spacing-4">
              <Dialog.Title className="text-dls-h2 font-normal text-primary">
                {title}
              </Dialog.Title>
              {message && (
                <Dialog.Description className="text-dls-body text-secondary leading-relaxed max-h-32 overflow-y-auto whitespace-pre-wrap break-all">
                  {message}
                </Dialog.Description>
              )}
            </div>
          </div>
          
          {/* Close button */}
          <Dialog.Close asChild>
            <button
              disabled={isLoading}
              className="p-spacing-1 rounded hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close"
            >
              <CrossIcon className="size-3" aria-hidden="true" />
            </button>
          </Dialog.Close>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-spacing-2 p-spacing-4">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="px-spacing-4 py-spacing-2 text-dls-body border border-border-default rounded bg-bg-page hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-spacing-4 py-spacing-2 text-dls-body border border-transparent rounded focus:outline-none focus-visible:ring-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${confirmButtonClasses}`}
          >
            {isLoading ? (
              <span className="flex items-center gap-spacing-2">
                <svg className="animate-spin size-4" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </span>
            ) : confirmText}
          </button>
        </div>
      </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

ConfirmationModal.displayName = 'ConfirmationModal';

ConfirmationModal.propTypes = {
  /** Whether the modal is open */
  isOpen: PropTypes.bool,
  /** Modal title */
  title: PropTypes.string.isRequired,
  /** Modal message/description */
  message: PropTypes.string,
  /** Text for the confirm button */
  confirmText: PropTypes.string,
  /** Text for the cancel button */
  cancelText: PropTypes.string,
  /** Visual variant - 'default' or 'destructive' */
  variant: PropTypes.oneOf(['default', 'destructive']),
  /** Callback when confirm button is clicked */
  onConfirm: PropTypes.func,
  /** Callback when cancel button is clicked */
  onCancel: PropTypes.func,
  /** Callback when modal is closed */
  onClose: PropTypes.func,
  /** Whether an async operation is in progress */
  isLoading: PropTypes.bool,
};

export default ConfirmationModal;
